import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import { Button,Card,Checkbox,DialogActions,DialogContent,DialogTitle,FormControlLabel,FormGroup,InputAdornment,Stack,TextField,Typography } from "@mui/material";
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import { useMutation,useQuery } from "@tanstack/react-query";
import { useContext,useEffect,useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { NewFormContext } from "../../../context/NewFormContext";
import AttToken from "../../../helpers/attToken";
import { mensagemErroAtualizacaoUrlPublica } from "../../../helpers/fluxo";
import Checklists from "../../../service/Checklists";
import Files from "../../../service/Files";
import Fluxo from "../../../service/Fluxo";
import { ChecklistSubitem,ChecklistType } from "../../../types/checklist/types";
import { uploadFilesV2 } from "../../helpers/checklists";

const uploadService = new Files();
const modelService = new Checklists();
const fluxoService = new Fluxo();


type props = {
  open: boolean;
  handleCloseModal: Function;
  itemId: string;
  subitem: ChecklistSubitem;
  index?: number;
};

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
}));

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1
});

export default function Help({ open, handleCloseModal, itemId, subitem, index }: props) {
  const { t } = useTranslation('translation');
  const [image, setImage] = useState<File[]>([]);
  const { modelo, setModelo } = useContext(NewFormContext);

  async function convertImages(files: File[], id: string) {
    const imagensConvertidas: File[] = [];
    const referencias: string[] = [];

    for (const file of files) {
      const newName = `${crypto.randomUUID()}.webp`;
      const newFile = new File([file], newName, { type: file.type });

      imagensConvertidas.push(newFile);
      referencias.push(`help/${id}/${newName}`);
    }

    return { imagensConvertidas, referencias };
  }

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['DOWNLOAD_FILES_V2'],
    queryFn: async () => {
      const token = await AttToken();
      if (token) {
        const response = await uploadService.downloadFiles(token, 'web', '-', subitem.help?.foto ? [subitem.help.foto] : []);
        if (response.urls) {
          return response.urls;
        }
      }
    },
    enabled: (subitem.help?.foto as string)?.length > 0 && open,
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
  });

  useEffect(() => {
    async function loadImage() {
      try {
        if (!open || !subitem.help?.foto || !data?.length) return;
        const response = await fetch(data[0]);

        if (!response.ok) {
          throw new Error(`Erro HTTP ${response.status}`);
        }

        const blob = await response.blob();

        const file = new File(
          [blob],
          "image.webp",
          { type: blob.type }
        );

        setImage([file]);
      } catch (error) {
        throw new Error(`Erro ao carregar imagem: ${error}`);
      }
    }
    loadImage();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { mutate, isPending, isError, isSuccess, error: updateError } = useMutation({
    mutationKey: ['UPDATE_MODEL'],
    mutationFn: async (novoModelo: ChecklistType) => {
      try {
        toast.info(`${t('checklists.savingChanges')}`, { toastId: 'checklists.savingChanges' });
        const token = await AttToken();
        if (token) {
          const response = await modelService.changeForm(token, novoModelo);
          const aliasEmpresa = novoModelo.urlPublica?.aliasEmpresa?.trim();
          const aliasChecklist = novoModelo.urlPublica?.aliasChecklist?.trim();
          if (aliasEmpresa && aliasChecklist) {
            try {
              await fluxoService.atualizarUrlPublicaChecklist(token, novoModelo.id, { aliasEmpresa, aliasChecklist });
            } catch (error) {
              throw new Error(mensagemErroAtualizacaoUrlPublica(error, t));
            }
          }
          return response;
        }
      } catch (error) {
        throw error;
      }
    },
    onSuccess: () => {
      handleCloseModal();
    }
  });

  useEffect(() => {
    if (isSuccess) {
      toast.success(`${t('checklists.changesSaved')}`);
    }
    if (isError) {
      toast.error(updateError instanceof Error ? updateError.message : t('checklists.error'));
    }
  }, [isSuccess, isError, t, updateError]);

  function handleRemoveImage() {
    setImage([]);
    setModelo((prev: ChecklistType) => ({
      ...prev,
      itens: prev.itens.map((item) =>
        item.id === itemId
          ? {
            ...item,
            subitens: item.subitens.map((subitem) =>
              subitem.id === index
                ? { ...subitem, help: { ...subitem.help, choose: 'foto', foto: '' } }
                : subitem
            )
          }
          : item
      )
    }));
  }

  function handleSelectMidia(value: string) {
    setModelo((prev: ChecklistType) => ({
      ...prev,
      itens: prev.itens.map((item) =>
        item.id === itemId
          ? {
            ...item,
            subitens: item.subitens.map((subitem) =>
              subitem.id === index
                ? { ...subitem, help: { ...subitem.help, choose: value, foto: '', video: '' } }
                : subitem
            )
          }
          : item
      )
    }));
    setImage([]);
  }

  function handleLink(eve: React.ChangeEvent<HTMLInputElement>) {
    setModelo((prev: ChecklistType) => ({
      ...prev,
      itens: prev.itens.map((item) =>
        item.id === itemId
          ? {
            ...item,
            subitens: item.subitens.map((subitem) =>
              subitem.id === index
                ? { ...subitem, help: { ...subitem.help, video: eve.target.value, foto: '' } }
                : subitem
            )
          }
          : item
      )
    }));
  }


  async function handleImage() {
    if (image?.length) {
      const { imagensConvertidas, referencias } = await convertImages(image, modelo?.id as string);
      await uploadFilesV2(imagensConvertidas, `help/${modelo?.id}`);
      const novoModelo: ChecklistType = {
        ...modelo!,
        itens: modelo!.itens.map((item) =>
          item.id === itemId
            ? {
              ...item,
              subitens: item.subitens.map((subitem) =>
                subitem.id === index
                  ? {
                    ...subitem,
                    help: {
                      ...subitem.help,
                      video: "",
                      foto: referencias[0],
                    },
                  }
                  : subitem
              ),
            }
            : item
        ),
      };
      setModelo(novoModelo);
      mutate(novoModelo);
    }
  }

  function textHelp(eve: React.ChangeEvent<HTMLInputElement>) {
    setModelo((prev: ChecklistType) => ({
      ...prev,
      itens: prev.itens.map((item) =>
        item.id === itemId
          ? {
            ...item,
            subitens: item.subitens.map((subitem) =>
              subitem.id === index
                ? { ...subitem, help: { ...subitem.help, comentario: eve.target.value } }
                : subitem
            )
          }
          : item
      )
    }));
  }

  return (
    <BootstrapDialog
      onClose={() => handleCloseModal()}
      aria-labelledby="customized-dialog-title"
      open={open}
    >
      <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
        Ajuda subitem
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={() => handleCloseModal()}
        sx={(theme) => ({
          position: 'absolute',
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        })}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers>
        <Card variant="outlined">
          <Stack direction='row' alignItems='center' spacing={1} padding={2}>
            <HelpOutlineIcon />
            <Typography variant="body1" padding={2}>
              Use essas configurações para indicar melhores instruções para o preenchimento do formulário.
            </Typography>
          </Stack>
        </Card>
        <Stack direction='column' spacing={2} alignItems='baseline' justifyContent='flex-start' padding={2}>
          <Typography variant="h6" fontWeight={600} color='textDisabled'>Informações Visuais</Typography>
          <Stack direction='row' alignItems='center' justifyContent='space-between' spacing={4} width='100%'>
            <FormGroup>
              <FormControlLabel
                control={<Checkbox onChange={(eve) => eve.target.checked
                  ? handleSelectMidia('foto')
                  : handleSelectMidia('')}
                  checked={subitem.help?.choose === 'foto'} />}
                label="Enviar uma foto" />
              <FormControlLabel
                control={<Checkbox onChange={(eve) => eve.target.checked
                  ? handleSelectMidia('video')
                  : handleSelectMidia('')}
                  checked={subitem.help?.choose === 'video'} />}
                label="Enviar um vídeo" />
            </FormGroup>
            {subitem.help?.choose === 'video'
              && <TextField
                value={subitem.help?.video || ''}
                onChange={handleLink}
                variant="outlined"

                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <InsertLinkIcon />
                      </InputAdornment>
                    ),
                  },
                }} label='link do vídeo' />}
            {subitem.help?.choose === 'foto' && <>
              {!image?.length
                ? <Button
                  color="secondary"
                  component="label"
                  variant="contained"
                  endIcon={<AddPhotoAlternateIcon />}
                >
                  Enviar fotos
                  <VisuallyHiddenInput
                    accept="image/jpg, .jpeg, .png, .gif, .tiff, .tif, .svg, .webp, .heic, .heif, .bmp"
                    type="file"
                    onChange={(event) => {
                      if (!event.target.files) return;
                      const novos = Array.from(event.target.files);
                      const tamanhoMaxIndividual = 20 * 1024 * 1024; // 20MB

                      // Filtra arquivos válidos
                      const validos = novos.filter((file) => {
                        // Verifica se o tipo do arquivo está dentro dos tipos permitidos
                        const tipoPermitido = /^(image\/jpg|image\/jpeg|image\/png|image\/gif|image\/tiff|image\/svg\+xml|image\/webp|image\/heic|image\/heif|image\/bmp)$/i;
                        if (!tipoPermitido.test(file.type)) {
                          toast.warning(`O arquivo "${file.name}" tem um tipo não aceito. Tipos permitidos: JPG, JPEG, PNG, GIF, TIFF, SVG, WebP, HEIC, HEIF, BMP.`);
                          return false;
                        }

                        // Verifica se o tamanho do arquivo é maior que o permitido
                        if (file.size > tamanhoMaxIndividual) {
                          toast.warning(`A imagem "${file.name}" ultrapassa 20MB e foi ignorado.`);
                          return false;
                        }

                        return true;
                      });

                      // Atualiza o estado com as imagens válidas
                      setImage((prevImages) => [...prevImages, ...validos]);
                    }}
                  />
                </Button>
                : <>
                  {
                    isLoading || isFetching ? <Typography>Carregando imagem...</Typography> :
                      image?.map((img, index) => (
                        <div key={index}>
                          <IconButton onClick={handleRemoveImage} color="error" sx={{
                            position: 'absolute',
                          }}>
                            <RemoveCircleIcon />
                          </IconButton>
                          <img
                            width={150}
                            height={150}
                            style={{ objectFit: 'cover', maxHeight: '150px', maxWidth: '150px' }}
                            srcSet={`${img}?w=150&h=150&fit=crop&auto=format&dpr=2 2x`}
                            src={URL.createObjectURL(img)}
                            alt={img.name}
                            loading="lazy" />
                        </div>))}
                </>}
            </>}
          </Stack>
          <Typography variant="h6" fontWeight={600} color='textDisabled'>Descrição escrita</Typography>
          <TextField
            value={subitem.help?.comentario || ''}
            onChange={textHelp}
            multiline
            rows={4}
            label="Insira informações aqui"
            variant="outlined"
            fullWidth />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={isPending} onClick={() => {
          if (image.length > 0) {
            handleImage();
          } else handleCloseModal();
        }}>
          {t('save')}
        </Button>
      </DialogActions>
    </BootstrapDialog>
  );
}
